import HttpClient from '@/repositories/clients/httpClient';

/**
 * Service for uploading and downloading photo sets to/from the backend.
 * Photos are uploaded one by one to avoid size/corruption issues.
 */
const frontPicturesService = {

  /**
   * Initialize a new photo set on the backend.
   * This will delete any previous set for the current user.
   * @param {number} expectedCount - Total number of photos to attempt uploading
   * @returns {Promise<object>} - { setId, expectedCount }
   */
  async initSet(expectedCount) {
    const response = await HttpClient.post('front-pictures/init', { expectedCount });
    return response.data.data;
  },

  /**
   * Upload a single photo (as Blob/File) to the backend.
   * @param {Blob|File} photoBlob - The photo blob to upload
   * @param {string} filename - The filename to use
   * @returns {Promise<object>} - { success, uploadedCount, expectedCount }
   */
  async uploadPhoto(photoBlob, filename) {
    const formData = new FormData();
    formData.append('photo', photoBlob, filename);

    const response = await HttpClient.post('front-pictures/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  },

  /**
   * Get info about the existing photo set for a promoter.
   * @param {number|null} promoterId - If null, uses the connected user
   * @returns {Promise<object|null>} - { id, createdAt, uploadedCount, expectedCount } or null
   */
  async getSetInfo(promoterId = null) {
    const params = promoterId ? { promoterId } : {};
    const response = await HttpClient.get('front-pictures/info', { params });
    return response.data.data?.set || null;
  },

  /**
   * Get the list of photo filenames in the set.
   * @param {number|null} promoterId
   * @returns {Promise<string[]>}
   */
  async listPhotos(promoterId = null) {
    const params = promoterId ? { promoterId } : {};
    const response = await HttpClient.get('front-pictures/list', { params });
    return response.data.data?.photos || [];
  },

  /**
   * Download a single photo by filename (as Blob).
   * @param {string} filename
   * @param {number|null} promoterId
   * @returns {Promise<Blob>}
   */
  async downloadPhoto(filename, promoterId = null) {
    const params = promoterId ? { promoterId } : {};
    const response = await HttpClient.get(`front-pictures/download/${filename}`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  }
};

export default frontPicturesService;

